import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';
import { prefs } from '../lib/storage';

export type GeoStatus = 'idle' | 'asking' | 'granted' | 'denied' | 'unavailable';
export interface Coords {
  lat: number;
  lng: number;
}
interface GeoApi {
  coords: Coords | null;
  status: GeoStatus;
  /** true when the user previously dismissed the prompt */
  dismissed: boolean;
  request: () => void;
  dismiss: () => void;
  /** query-string fragment "&lat=..&lng=.." or '' */
  param: Record<string, number | undefined>;
}

const Ctx = createContext<GeoApi | null>(null);

export function GeoProvider({ children }: { children: ComponentChildren }) {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [status, setStatus] = useState<GeoStatus>(typeof navigator !== 'undefined' && 'geolocation' in navigator ? 'idle' : 'unavailable');
  const [dismissed, setDismissed] = useState<boolean>(() => prefs.geoDismissed());

  const request = useCallback(() => {
    if (!('geolocation' in navigator)) return setStatus('unavailable');
    setStatus('asking');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: +pos.coords.latitude.toFixed(5), lng: +pos.coords.longitude.toFixed(5) });
        setStatus('granted');
        prefs.setGeoDismissed(false);
        setDismissed(false);
      },
      () => setStatus('denied'),
      { timeout: 10000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);

  const dismiss = useCallback(() => {
    prefs.setGeoDismissed(true);
    setDismissed(true);
  }, []);

  const value = useMemo<GeoApi>(
    () => ({
      coords,
      status,
      dismissed,
      request,
      dismiss,
      param: coords ? { lat: coords.lat, lng: coords.lng } : {},
    }),
    [coords, status, dismissed, request, dismiss],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGeo(): GeoApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useGeo outside GeoProvider');
  return v;
}
